import { DeliveryEvents } from '@theme/delivery-core';
import { validateDeliveryTimeMetaobject } from '@theme/delivery-moment-validation-in-cart';
import { Component } from '@theme/component';

// Constants
const SELECTORS = {
  SUBMIT_BUTTON: '.delivery-times-drawer__utility-button-submit',
  BACK_BUTTON: '.delivery-times-drawer__utility-button-back',
  POSTCODE_BUTTON: '.delivery-times-drawer__postcode-button',
  DELIVERY_SUBMIT_BUTTON: '#delivery-submit-button',
  BUTTON_TEXT_SPAN: '.delivery-submit-button-text',
  DAY_BUTTON: '.delivery-times-drawer__day-button',
  TIMESLOT_CARD: '.delivery-times-drawer__timeslot-card',
  SELECTED_DAY_BUTTON: '.delivery-times-drawer__day-button--selected',
  SELECTED_TIMESLOT_CARD: '.delivery-times-drawer__timeslot-card--selected',
  DISABLED_TIMESLOT_CARD: '.delivery-times-drawer__timeslot-card--disabled'
};

const CSS_CLASSES = {
  SELECTED_DAY: 'delivery-times-drawer__day-button--selected',
  SELECTED_TIMESLOT: 'delivery-times-drawer__timeslot-card--selected',
  DISABLED_TIMESLOT: 'delivery-times-drawer__timeslot-card--disabled',
  DISABLED_BUTTON: 'delivery-times-drawer__utility-button--disabled'
};

const DATA_ATTRIBUTES = {
  LISTENER_ATTACHED: 'data-listener-attached',
  DAY: 'data-day',
  METAOBJECT_ID: 'data-metaobjectId',
  METAOBJECT_HANDLE: 'data-metaobjectHandle',
  VERZENDMETHODE_HANDLE: 'data-verzendmethodeHandle',
  TIME_SLOT: 'data-timeSlot',
  BEGIN_TIME: 'data-beginTime',
  END_TIME: 'data-endTime',
  HAS_SELECTED_DELIVERY_TIME: 'data-hasSelectedDeliveryTime'
};

const BUTTON_TEXTS = {
  INITIAL: 'Kies je bezorgdag',
  DAY_SELECTED: 'Kies een uurslot',
  COMPLETE: 'Bezorgmoment kiezen'
};

const MESSAGES = {
  SELECT_TIMESLOT: 'Selecteer eerst een bezorgmoment',
  INVALID_TIMESLOT: 'Error: Invalid timeslot data',
  SELECTION_SUCCESS: 'Delivery time selected successfully',
  SELECTION_ERROR: 'Error selecting delivery time. Please try again.'
};

const CONSOLE_PREFIX = '[DELIVERY-DRAWER]';

// Helper Functions
/**
 * @param {any} level - Log level (log, warn, error)
 * @param {string} message - Message to log
 * @param {...any} args - Additional arguments
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`🔍 ${CONSOLE_PREFIX} ${message}`, ...args);
}

/**
 * Get element by selector with type safety
 * @param {HTMLElement} context - The context element to search within
 * @param {string} selector - CSS selector
 * @returns {HTMLElement|null} - The element or null
 */
function getElement(context, selector) {
  const element = context.querySelector(selector);
  return element instanceof HTMLElement ? element : null;
}

/**
 * Get all elements by selector with type safety
 * @param {HTMLElement} context - The context element to search within
 * @param {string} selector - CSS selector
 * @returns {HTMLElement[]} - Array of elements
 */
function getElements(context, selector) {
  return Array.from(context.querySelectorAll(selector)).filter(el => el instanceof HTMLElement);
}

/**
 * Remove class from all elements
 * @param {HTMLElement[]} elements - Array of elements
 * @param {string} className - Class name to remove
 */
function removeClassFromAll(elements, className) {
  elements.forEach(el => el.classList.remove(className));
}

/**
 * Add class to element
 * @param {HTMLElement} element - The element
 * @param {string} className - Class name to add
 */
function addClass(element, className) {
  element.classList.add(className);
}

/**
 * Remove class from element
 * @param {HTMLElement} element - The element
 * @param {string} className - Class name to remove
 */
function removeClass(element, className) {
  element.classList.remove(className);
}

/**
 * Set display style for element
 * @param {HTMLElement} element - The element
 * @param {string} display - Display value
 */
function setDisplay(element, display) {
  element.style.display = display;
}

/**
 * Hide element
 * @param {HTMLElement} element - The element
 */
function hideElement(element) {
  setDisplay(element, 'none');
}

/**
 * Show element as flex
 * @param {HTMLElement} element - The element
 */
function showElement(element) {
  setDisplay(element, 'flex');
}

/**
 * Check if element has class
 * @param {HTMLElement} element - The element
 * @param {string} className - Class name to check
 * @returns {boolean} - True if element has the class
 */
function hasClass(element, className) {
  return element.classList.contains(className);
}

/**
 * Get dataset value safely
 * @param {HTMLElement} element - The element
 * @param {string} key - Dataset key
 * @returns {string|undefined} - Dataset value or undefined
 */
function getDatasetValue(element, key) {
  return element.dataset[key];
}

/**
 * Open postal code drawer
 */
function openPostalCodeDrawer() {
  if (/** @type {any} */ (window).CombinedDrawers.openPostalCode()) {
    /** @type {any} */ (window).CombinedDrawers.openPostalCode();
  }
}

/**
 * Delivery times drawer component
 */
/**
 * A custom element that manages the collection footer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} closeButton - The next button element.
 *
 * @extends {Component<Refs>}
 */
export class DeliveryTimesDrawerComponent extends Component {
  connectedCallback() {
    try {
      super.connectedCallback();
      this.setupEventListeners();
      this.initializeDayTimeslotInteraction();
      this.storeButtonTexts();
      
      // Run validation if there's a selected delivery time
      if (this.dataset.hasSelectedDeliveryTime === 'true') {
        validateDeliveryTimeMetaobject();
      }
    } catch (error) {
      throw error;
    }
  }

  setupEventListeners() {
    try {
      const submitButton = getElement(this, SELECTORS.SUBMIT_BUTTON);
      const closeButton = this.refs.closeButton;

      // Use event delegation for buttons that might be recreated
      this.addEventListener('click', (e) => {
        const target = /** @type {Element} */ (e.target);
        if (target.matches(SELECTORS.BACK_BUTTON) || target.matches(SELECTORS.POSTCODE_BUTTON)) {
          // this.close();
          openPostalCodeDrawer();
        }
      });

      if (submitButton) {
        submitButton.addEventListener('click', () => this.handleSubmit());
        // Initialize submit button state
        this.updateSubmitButtonState();
      }

      if (closeButton) {
        closeButton.addEventListener('click', (e) => {
          this.close();
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Store button texts from data attributes
   */
  storeButtonTexts() {
    this.dataset.initialButtonText = this.dataset.initialButtonText || BUTTON_TEXTS.INITIAL;
    this.dataset.daySelectedButtonText = this.dataset.daySelectedButtonText || BUTTON_TEXTS.DAY_SELECTED;
    this.dataset.completeButtonText = this.dataset.completeButtonText || BUTTON_TEXTS.COMPLETE;
  }

  /**
   * Check if both day and timeslot are selected
   */
  isSelectionComplete() {
    const selectedDay = getElement(this, SELECTORS.SELECTED_DAY_BUTTON);
    const selectedTimeslot = getElement(this, SELECTORS.SELECTED_TIMESLOT_CARD);
    return selectedDay && selectedTimeslot;
  }

  /**
   * Check if a day is selected
   */
  hasDaySelected() {
    const selectedDay = getElement(this, SELECTORS.SELECTED_DAY_BUTTON);
    return !!selectedDay;
  }

  /**
   * Check if a timeslot is selected
   */
  hasTimeslotSelected() {
    const selectedTimeslot = getElement(this, SELECTORS.SELECTED_TIMESLOT_CARD);
    return !!selectedTimeslot;
  }

  /**
   * Update button text based on current selection state
   * @param {boolean} hasDaySelected - Whether a day is selected
   * @param {boolean} hasTimeslotSelected - Whether a timeslot is selected
   */
  updateButtonText(hasDaySelected, hasTimeslotSelected) {
    const submitButton = getElement(this, SELECTORS.DELIVERY_SUBMIT_BUTTON);
    const buttonTextSpan = submitButton?.querySelector(SELECTORS.BUTTON_TEXT_SPAN);
    
    if (buttonTextSpan) {
      let buttonText = this.dataset.initialButtonText || BUTTON_TEXTS.INITIAL;
      
      if (hasDaySelected && hasTimeslotSelected) {
        buttonText = this.dataset.completeButtonText || BUTTON_TEXTS.COMPLETE;
      } else if (hasDaySelected) {
        buttonText = this.dataset.daySelectedButtonText || BUTTON_TEXTS.DAY_SELECTED;
      }
      
      buttonTextSpan.textContent = buttonText;
    }
  }

  /**
   * Update submit button state based on selection completeness
   */
  updateSubmitButtonState() {
    const submitButton = getElement(this, SELECTORS.SUBMIT_BUTTON);
    if (submitButton) {
      const isComplete = this.isSelectionComplete();
      const hasDaySelected = this.hasDaySelected();
      const hasTimeslotSelected = this.hasTimeslotSelected();
      
      /** @type {HTMLButtonElement} */ (submitButton).disabled = !isComplete;
      
      // Add visual feedback for disabled state
      if (isComplete) {
        removeClass(submitButton, CSS_CLASSES.DISABLED_BUTTON);
      } else {
        addClass(submitButton, CSS_CLASSES.DISABLED_BUTTON);
      }
      
      // Update button text based on current state
      this.updateButtonText(hasDaySelected, hasTimeslotSelected);
    }
  }

  /**
   * Remove existing event listeners to prevent duplicates
   */
  removeExistingListeners() {
    const existingListeners = this.querySelectorAll(`[${DATA_ATTRIBUTES.LISTENER_ATTACHED}]`);
    existingListeners.forEach(el => {
      const newEl = el.cloneNode(true);
      const parentNode = el.parentNode;
      if (parentNode) {
        parentNode.replaceChild(newEl, el);
      }
    });
  }

  /**
   * Handle day button click
   * @param {HTMLElement} clickedButton - The clicked day button
   * @param {HTMLElement[]} allDayButtons - All day buttons
   * @param {HTMLElement[]} allTimeslotCards - All timeslot cards
   */
  handleDayButtonClick(clickedButton, allDayButtons, allTimeslotCards) {
    // Remove selected state from all day buttons
    removeClassFromAll(allDayButtons, CSS_CLASSES.SELECTED_DAY);
    // Add selected state to clicked button
    addClass(clickedButton, CSS_CLASSES.SELECTED_DAY);

    const selectedDay = getDatasetValue(clickedButton, 'day');
  
    // Hide all timeslot cards first
    allTimeslotCards.forEach(card => {
      hideElement(card);
      removeClass(card, CSS_CLASSES.SELECTED_TIMESLOT);
    });

    // Show only timeslot cards for selected day
    const dayTimeslots = this.querySelectorAll(`[${DATA_ATTRIBUTES.DAY}="${selectedDay}"]`);
    dayTimeslots.forEach((card, index) => {
      if (card instanceof HTMLElement && hasClass(card, 'delivery-times-drawer__timeslot-card')) {
        showElement(card);
        // Enable the card and remove disabled state
        removeClass(card, CSS_CLASSES.DISABLED_TIMESLOT);
        // Only select first timeslot if no timeslot is currently selected
        if (index === 0 && !getElement(this, SELECTORS.SELECTED_TIMESLOT_CARD)) {
          addClass(card, CSS_CLASSES.SELECTED_TIMESLOT);
        }
      }
    });
    
    // Update submit button state after day selection
    this.updateSubmitButtonState();
  }

  /**
   * Handle timeslot card click
   * @param {HTMLElement} clickedCard - The clicked timeslot card
   * @param {HTMLElement[]} allTimeslotCards - All timeslot cards
   */
  handleTimeslotCardClick(clickedCard, allTimeslotCards) {
    // Only allow selection if card is not disabled
    if (hasClass(clickedCard, CSS_CLASSES.DISABLED_TIMESLOT)) {
      return;
    }
    
    // Remove selected state from all timeslot cards
    removeClassFromAll(allTimeslotCards, CSS_CLASSES.SELECTED_TIMESLOT);
    // Add selected state to clicked card
    addClass(clickedCard, CSS_CLASSES.SELECTED_TIMESLOT);
    
    // Update submit button state after timeslot selection
    this.updateSubmitButtonState();
  }

  /**
   * Initialize day and timeslot interaction functionality
   */
  initializeDayTimeslotInteraction() {
    try {
      // Remove existing event listeners to prevent duplicates
      this.removeExistingListeners();

      // Initialize selection based on cart attributes
      this.initializeSelection();

      // Handle day selection
      const dayButtons = getElements(this, SELECTORS.DAY_BUTTON);
      const timeslotCards = getElements(this, SELECTORS.TIMESLOT_CARD);

      dayButtons.forEach(button => {
        button.setAttribute(DATA_ATTRIBUTES.LISTENER_ATTACHED, 'true');
        button.addEventListener('click', () => {
          this.handleDayButtonClick(button, dayButtons, timeslotCards);
        });
      });

      // Handle timeslot selection
      timeslotCards.forEach(card => {
        card.setAttribute(DATA_ATTRIBUTES.LISTENER_ATTACHED, 'true');
        card.addEventListener('click', () => {
          this.handleTimeslotCardClick(card, timeslotCards);
        });
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Show timeslots for a specific day
   * @param {string} selectedDay - The selected day
   * @param {HTMLElement[]} allTimeslotCards - All timeslot cards
   * @param {boolean} enableCards - Whether to enable the cards
   */
  showTimeslotsForDay(selectedDay, allTimeslotCards, enableCards = true) {
    // Hide all timeslots first
    allTimeslotCards.forEach(card => hideElement(card));
    
    // Show only timeslots for the selected day
    const dayTimeslots = this.querySelectorAll(`[${DATA_ATTRIBUTES.DAY}="${selectedDay}"]`);
    dayTimeslots.forEach(card => {
      if (card instanceof HTMLElement && hasClass(card, 'delivery-times-drawer__timeslot-card')) {
        showElement(card);
        if (enableCards) {
          removeClass(card, CSS_CLASSES.DISABLED_TIMESLOT);
        } else {
          addClass(card, CSS_CLASSES.DISABLED_TIMESLOT);
        }
      }
    });
  }

  /**
   * Initialize selection with existing delivery time
   * @param {HTMLElement[]} dayButtons - All day buttons
   * @param {HTMLElement[]} timeslotCards - All timeslot cards
   */
  initializeSelectionWithExistingTime(dayButtons, timeslotCards) {
    const selectedTimeslot = getElement(this, SELECTORS.SELECTED_TIMESLOT_CARD);
    if (selectedTimeslot) {
      const selectedDay = getDatasetValue(selectedTimeslot, 'day');
      if (selectedDay) {
        const correspondingDayButton = this.querySelector(`[${DATA_ATTRIBUTES.DAY}="${selectedDay}"]`);
        if (correspondingDayButton instanceof HTMLElement) {
          addClass(correspondingDayButton, CSS_CLASSES.SELECTED_DAY);
        }
        
        this.showTimeslotsForDay(selectedDay, timeslotCards, true);
      }
    }
  }

  /**
   * Initialize selection without existing delivery time
   * @param {HTMLElement[]} dayButtons - All day buttons
   * @param {HTMLElement[]} timeslotCards - All timeslot cards
   */
  initializeSelectionWithoutExistingTime(dayButtons, timeslotCards) {
    const firstDayButton = dayButtons[0];
    if (firstDayButton) {
      const selectedDay = getDatasetValue(firstDayButton, 'day');
      if (selectedDay) {
        this.showTimeslotsForDay(selectedDay, timeslotCards, false);
      }
    }
  }

  /**
   * Initialize the selection state for days and timeslots
   */
  initializeSelection() {
    const dayButtons = getElements(this, SELECTORS.DAY_BUTTON);
    const timeslotCards = getElements(this, SELECTORS.TIMESLOT_CARD);

    // Check if there's a selected delivery time in cart attributes
    const hasSelectedDeliveryTime = this.hasSelectedDeliveryTimeInCart();

    if (hasSelectedDeliveryTime) {
      this.initializeSelectionWithExistingTime(dayButtons, timeslotCards);
    } else {
      console.log('initializeSelectionWithoutExistingTime');
      this.initializeSelectionWithoutExistingTime(dayButtons, timeslotCards);
    }
    
    // If there's a selected delivery time, make sure the corresponding day's timeslots are enabled
    if (hasSelectedDeliveryTime) {
      const selectedTimeslot = getElement(this, SELECTORS.SELECTED_TIMESLOT_CARD);
      if (selectedTimeslot) {
        const selectedDay = getDatasetValue(selectedTimeslot, 'day');
        if (selectedDay) {
          const dayTimeslots = this.querySelectorAll(`[${DATA_ATTRIBUTES.DAY}="${selectedDay}"]`);
          dayTimeslots.forEach(card => {
            if (card instanceof HTMLElement && hasClass(card, 'delivery-times-drawer__timeslot-card')) {
              removeClass(card, CSS_CLASSES.DISABLED_TIMESLOT);
            }
          });
        }
      }
    }
    
    // Update submit button state after initialization
    this.updateSubmitButtonState();
  }

  /**
   * Check if there's a selected delivery time in cart attributes
   */
  hasSelectedDeliveryTimeInCart() {
    try {
      logMessage('log', 'Checking if there\'s a selected delivery time in cart attributes');

      // Check the data attribute set by Liquid
      const hasSelectedDeliveryTime = this.dataset.hasSelectedDeliveryTime === 'true';
      return hasSelectedDeliveryTime;
    } catch (error) {
      return false;
    }
  }

  /**
   * Extract time data from selected card
   * @param {HTMLElement} cardElement - The selected card element
   * @returns {any} - The extracted time data
   */
  extractTimeDataFromCard(cardElement) {
    try {
      const timeData = {
        metaobjectId: getDatasetValue(cardElement, 'metaobjectId'),
        metaobjectHandle: getDatasetValue(cardElement, 'metaobjectHandle'),
        verzendmethodeHandle: getDatasetValue(cardElement, 'verzendmethodeHandle'),
        deliveryDay: getDatasetValue(cardElement, 'day'),
        timeSlot: getDatasetValue(cardElement, 'timeSlot'),
        beginTime: getDatasetValue(cardElement, 'beginTime'),
        endTime: getDatasetValue(cardElement, 'endTime')
      };

      return timeData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Validate time data
   * @param {any} timeData - The time data to validate
   * @returns {boolean} - True if valid
   */
  validateTimeData(timeData) {
    return !!timeData.metaobjectId;
  }

  async handleSubmit() {
    try {
      const selectedCard = getElement(this, SELECTORS.SELECTED_TIMESLOT_CARD);
      if (!selectedCard) {
        logMessage('warn', 'No timeslot selected for submission');
        alert(MESSAGES.SELECT_TIMESLOT);
        return;
      }

      // Get the metaobject ID and handle from the selected card
      const cardElement = /** @type {HTMLElement} */ (selectedCard);
      const timeData = this.extractTimeDataFromCard(cardElement);

      if (!this.validateTimeData(timeData)) {
        logMessage('warn', 'Invalid timeslot data during submission', timeData);
        alert(MESSAGES.INVALID_TIMESLOT);
        return;
      }

      // Get cart sections that need to be updated using DeliveryCartAttributes utility
      const cartSections = /** @type {any} */ (window).DeliveryCartAttributes?.getCartSectionsToUpdate() || [];

      const success = await /** @type {any} */ (window).DeliveryCartAttributes?.setDeliveryTime(timeData, cartSections);

      if (success) {
        // Close drawer
        this.close();
        logMessage('log', MESSAGES.SELECTION_SUCCESS);
        
        // Notify delivery manager of final time selection (triggers filtering and cart compatibility)
        document.dispatchEvent(new CustomEvent(DeliveryEvents.DELIVERY_TIME_SELECTED, {
          detail: timeData
        }));
      } else {
        logMessage('error', 'Failed to submit delivery time');
        alert(MESSAGES.SELECTION_ERROR);
      }
    } catch (error) {
      logMessage('error', 'Error during delivery time submission:', error);
      alert(MESSAGES.SELECTION_ERROR);
    }
  }

  open() {
    // Open the combined drawers -> delivery times drawer
    /** @type {any} */ (window).CombinedDrawers.openDelivery();
  }

  close() {
    // Close the combined drawers
    /** @type {any} */ (window).CombinedDrawers.close();
  }
}

// Register custom element
if (!customElements.get('delivery-times-drawer-component')) {
  customElements.define('delivery-times-drawer-component', DeliveryTimesDrawerComponent);
}

// Export for global access
if (typeof window !== "undefined") {
  /** @type {any} */ (window).DeliveryDrawer = {
    DeliveryTimesDrawerComponent
  };

  // Backward compatibility
  /** @type {any} */ (window).DeliveryTimesDrawerComponent = DeliveryTimesDrawerComponent;
}
